// ---------------------------------------------------------------------------
// Estratto VERBATIM da TabPreventivi.tsx (Task #3874 — split interno Fase 2).
// Nessuna modifica logica: JSX spostato tale e quale, stato e handler restano
// nel componente padre e arrivano via props esplicite.
// ---------------------------------------------------------------------------
import type { Dispatch, SetStateAction } from 'react';
import { Search, Plus, X } from 'lucide-react';
import api from '@/lib/api';
import { CRM_BASE } from '../types';
import type { NewQuoteDraft } from '../types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterClientListRow as MasterClient,
  CrmMasterPriceList as PriceList,
  CrmMasterPriceListItem as PriceListItem,
  CrmMasterPipelineEntry as PipelineEntry,
  CrmMasterProduct as CrmProduct,
  CrmMasterDuplicateWarning as DuplicateWarning,
} from '@easyflow/shared';
import { ITALIAN_VAT_RATES, VatSelect, ModalWrapper } from '../shared';

interface CreateQuoteModalProps {
  showCreate: boolean;
  setShowCreate: Dispatch<SetStateAction<boolean>>;
  clientSearch: string;
  setClientSearch: Dispatch<SetStateAction<string>>;
  clientType: 'tenant' | 'lead' | 'client' | '';
  setClientType: Dispatch<SetStateAction<'tenant' | 'lead' | 'client' | ''>>;
  newQuote: NewQuoteDraft;
  setNewQuote: Dispatch<SetStateAction<NewQuoteDraft>>;
  filteredClients: MasterClient[];
  filteredLeads: PipelineEntry[];
  setDuplicateWarning: Dispatch<SetStateAction<DuplicateWarning[] | null>>;
  setPendingLeadSelection: Dispatch<SetStateAction<PipelineEntry | null>>;
  selectedPriceListId: string;
  setSelectedPriceListId: Dispatch<SetStateAction<string>>;
  setSelectedPLItems: Dispatch<SetStateAction<PriceListItem[]>>;
  selectPriceList: (plId: string) => Promise<void>;
  priceLists: PriceList[];
  productSearch: string;
  setProductSearch: Dispatch<SetStateAction<string>>;
  plItemsLoading: boolean;
  filteredPLProducts: PriceListItem[];
  addItem: (product: CrmProduct) => Promise<void>;
  updateItem: (idx: number, field: string, value: string | number) => void;
  removeItem: (idx: number) => void;
  totals: { net: string; vat: string; gross: string };
  saving: boolean;
  handleCreate: () => Promise<void>;
}

export function CreateQuoteModal({
  showCreate, setShowCreate, clientSearch, setClientSearch, clientType, setClientType,
  newQuote, setNewQuote, filteredClients, filteredLeads, setDuplicateWarning, setPendingLeadSelection,
  selectedPriceListId, setSelectedPriceListId, setSelectedPLItems, selectPriceList, priceLists,
  productSearch, setProductSearch, plItemsLoading, filteredPLProducts,
  addItem, updateItem, removeItem, totals, saving, handleCreate,
}: CreateQuoteModalProps) {
  return (
      <ModalWrapper open={showCreate} onClose={() => setShowCreate(false)} title="Nuovo Preventivo" wide>
        <div className="space-y-4">
          <div className="relative">
            <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Cliente / Lead</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                <input value={clientSearch} onChange={(e) => { setClientSearch(e.target.value); if (clientType) { setClientType(''); setNewQuote((q) => ({ ...q, tenant_id: '', pipeline_entry_id: '' })); } }}
                  placeholder="Cerca cliente o lead..."
                  className="w-full pl-9 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
              </div>
              {clientType && (
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${clientType === 'client' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {clientType === 'client' ? 'Cliente' : 'Lead'}
                  <button onClick={() => { setClientType(''); setClientSearch(''); setNewQuote((q) => ({ ...q, tenant_id: '', pipeline_entry_id: '' })); }} className="ml-1 hover:text-white"><X className="h-3 w-3" /></button>
                </span>
              )}
            </div>
            {clientSearch && !clientType && (filteredClients.length > 0 || filteredLeads.length > 0) && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-gray-700 border border-border dark:border-gray-600 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                {filteredClients.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider bg-muted/50 dark:bg-gray-800/50 sticky top-0">Clienti</div>
                    {filteredClients.slice(0, 5).map(c => (
                      <div key={`t-${c.id}`} onClick={() => { setNewQuote((q) => ({ ...q, tenant_id: '', pipeline_entry_id: c.id })); setClientSearch(c.company_name || c.contact_name || ''); setClientType('client'); }}
                        className="px-3 py-2 hover:bg-muted dark:hover:bg-gray-600 text-foreground dark:text-white text-sm cursor-pointer flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                        {c.company_name || c.contact_name || '-'}
                      </div>
                    ))}
                  </>
                )}
                {filteredLeads.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider bg-muted/50 dark:bg-gray-800/50 sticky top-0">Lead / Opportunità</div>
                    {filteredLeads.slice(0, 5).map(l => (
                      <div key={`l-${l.id}`} onClick={async () => {
                        try {
                          const params = new URLSearchParams();
                          if (l.company_name) params.set('company_name', l.company_name);
                          if (l.vat_number) params.set('vat_number', l.vat_number);
                          if (l.contact_email) params.set('contact_email', l.contact_email);
                          const dupRes = await api.get(`${CRM_BASE}/pipeline/check-duplicate?${params.toString()}`);
                          const dups: DuplicateWarning[] = (dupRes.success && dupRes.data && Array.isArray(dupRes.data)) ? (dupRes.data as DuplicateWarning[]).filter((d) => d.id !== l.id) : [];
                          if (dups.length > 0) {
                            setDuplicateWarning(dups);
                            setPendingLeadSelection(l);
                            return;
                          }
                        } catch {}
                        setNewQuote((q) => ({ ...q, tenant_id: '', pipeline_entry_id: l.id }));
                        setClientSearch(l.company_name || l.contact_name || 'Lead');
                        setClientType('lead');
                      }}
                        className="px-3 py-2 hover:bg-muted dark:hover:bg-gray-600 text-foreground dark:text-white text-sm cursor-pointer flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                        <span>{l.company_name || l.contact_name || 'Senza nome'}</span>
                        {l.stage_name && <span className="text-[10px] text-muted-foreground/70 ml-auto">{l.stage_name}</span>}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Listino Prezzi</label>
            <select value={selectedPriceListId} onChange={(e) => { if (e.target.value) void selectPriceList(e.target.value); else { setSelectedPriceListId(''); setSelectedPLItems([]); } }}
              className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
              <option value="">Seleziona listino...</option>
              {priceLists.map(pl => <option key={pl.id} value={pl.id}>{pl.name}{pl.is_default ? ' (Predefinito)' : ''}</option>)}
            </select>
          </div>

          {selectedPriceListId && (
            <div className="bg-muted/50 dark:bg-gray-700/30 rounded-xl border border-border dark:border-gray-600/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70">Prodotti — {priceLists.find(p => p.id === selectedPriceListId)?.name}</label>
                <div className="relative w-56">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
                  <input value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Filtra prodotti..."
                    className="w-full pl-8 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-1.5 text-xs" />
                </div>
              </div>
              <div className="overflow-auto max-h-64 rounded-lg border border-border dark:border-gray-600/50">
                <table className="w-full text-sm">
                  <thead className="bg-muted dark:bg-gray-800/50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Codice</th>
                      <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Prodotto</th>
                      <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Prezzo Base</th>
                      <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Prezzo Listino</th>
                      <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Sc%</th>
                      <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">IVA</th>
                      <th className="py-2 px-2 text-xs w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {plItemsLoading ? (
                      <tr><td colSpan={7} className="py-4 text-center text-muted-foreground/70 text-xs">Caricamento...</td></tr>
                    ) : filteredPLProducts.length > 0 ? filteredPLProducts.map((p) => {
                      const vatCode = String(p.vat_rate || p.product_vat_code || '22');
                      const vatLabel = ITALIAN_VAT_RATES.find(v => v.code === vatCode)?.label || `${vatCode}%`;
                      return (
                      <tr key={`pl-${p.product_id}`} className={`border-t border-gray-100 dark:border-gray-600/30 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer ${p.in_price_list ? '' : 'opacity-70'}`}
                        onClick={() => addItem({ id: p.product_id, name: p.product_name, net_price: p.price, vat_code: vatCode } as CrmProduct)}>
                        <td className="py-2 px-3 text-xs text-muted-foreground/70 font-mono">{p.product_code || '-'}</td>
                        <td className="py-2 px-3 text-foreground dark:text-white text-xs">
                          {p.product_name}
                          {Boolean(p.in_price_list) && <span className="ml-1.5 text-[9px] bg-blue-500/20 text-blue-400 px-1 py-0.5 rounded">Listino</span>}
                        </td>
                        <td className="py-2 px-3 text-right text-xs text-muted-foreground/70">{'€'} {Number(p.base_price || 0).toFixed(2)}</td>
                        <td className="py-2 px-3 text-right text-xs text-foreground dark:text-white font-medium">{'€'} {Number(p.price || 0).toFixed(2)}</td>
                        <td className="py-2 px-3 text-right text-xs text-muted-foreground/70">{Number(p.discount_percent || 0) > 0 ? `${Number(p.discount_percent)}%` : '-'}</td>
                        <td className="py-2 px-3 text-xs text-muted-foreground/70">{vatLabel}</td>
                        <td className="py-2 px-2">
                          <Plus className="h-4 w-4 text-blue-400" />
                        </td>
                      </tr>
                      );
                    }) : (
                      <tr><td colSpan={7} className="py-4 text-center text-muted-foreground/70 text-xs">Nessun prodotto disponibile</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-2 block">Righe Preventivo ({newQuote.items.length})</label>
            {newQuote.items.length > 0 && (
              <div className="grid mb-1.5" style={{ gridTemplateColumns: '1fr 4rem 6rem 4rem 10rem 5rem 1.5rem', gap: '0.5rem', alignItems: 'center' }}>
                <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider px-2">Descrizione</span>
                <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider text-center">Qtà</span>
                <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider text-right px-2">Prezzo €</span>
                <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider text-center">Sconto %</span>
                <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider px-1">IVA</span>
                <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider text-right px-1">Totale</span>
                <span></span>
              </div>
            )}
            {newQuote.items.map((item, idx: number) => (
              <div key={idx} className="grid mb-2" style={{ gridTemplateColumns: '1fr 4rem 6rem 4rem 10rem 5rem 1.5rem', gap: '0.5rem', alignItems: 'center' }}>
                <input value={item.description ?? ''} onChange={(e) => updateItem(idx, 'description', e.target.value)}
                  className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1.5 text-sm" placeholder="Descrizione" />
                <input value={item.quantity ?? ''} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} type="number"
                  className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1.5 text-sm text-center" placeholder="Qtà" />
                <input value={item.unit_price ?? ''} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} type="number"
                  className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1.5 text-sm text-right" placeholder="Prezzo" />
                <input value={item.discount_percent ?? ''} onChange={(e) => updateItem(idx, 'discount_percent', parseFloat(e.target.value) || 0)} type="number"
                  className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1.5 text-sm text-center" placeholder="Sc%" />
                <VatSelect value={String(item.vat_code || item.vat_rate || '22')} onChange={(v) => { updateItem(idx, 'vat_code', v); updateItem(idx, 'vat_rate', v); }} />
                <span className="text-xs text-foreground dark:text-foreground text-right px-1 tabular-nums">€ {(((parseFloat(String(item.quantity)) || 0) * (parseFloat(String(item.unit_price)) || 0)) * (1 - (parseFloat(String(item.discount_percent)) || 0) / 100)).toFixed(2)}</span>
                <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300 flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>
            ))}
            {newQuote.items.length === 0 && <p className="text-xs text-muted-foreground/70 text-center py-2">Seleziona un listino e clicca sui prodotti per aggiungerli</p>}
          </div>

          <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-4 space-y-1">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground dark:text-muted-foreground/70">Netto:</span><span className="text-foreground dark:text-white">€ {totals.net}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground dark:text-muted-foreground/70">IVA:</span><span className="text-foreground dark:text-white">€ {totals.vat}</span></div>
            <div className="flex justify-between text-sm font-semibold"><span className="text-muted-foreground dark:text-muted-foreground/60">Totale Lordo:</span><span className="text-foreground dark:text-white">€ {totals.gross}</span></div>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Note</label>
            <textarea value={newQuote.notes || ''} onChange={(e) => setNewQuote((q) => ({ ...q, notes: e.target.value }))}
              className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm h-16 resize-none" />
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
            <button onClick={handleCreate} disabled={saving || !newQuote.pipeline_entry_id}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
              {saving ? 'Creazione...' : 'Crea Preventivo'}
            </button>
          </div>
        </div>
      </ModalWrapper>
  );
}
